/**
 * Service for processing Non-AI application data from Crossover API
 * Filters out AI and PURE_AI groups and aggregates business application usage
 */

import type { 
  CrossoverTeamMemberActivity, 
  CrossoverGroup, 
  CrossoverApplication 
} from '../types';
import { calculateTimeStats } from './timeCalculationService';

export interface ApplicationUsage {
  applicationName: string;
  totalUsers: number;
  totalHours: number;
  category: string;
  percentage: number;
}

export interface TeamMemberApplications {
  userId: string;
  email: string;
  name: string;
  applications: {
    [appName: string]: {
      hours: number;
      category: string;
      frequency: number;
    };
  };
  totalNonAIHours: number;
  topApplication: string;
  applicationsByCategory: {
    [category: string]: { appName: string; hours: number }[];
  };
}

export interface NonAIDataResult {
  teamMemberApplications: TeamMemberApplications[];
  topApplications: ApplicationUsage[];
}

export class NonAIDataService {
  
  /**
   * Main processing function for Non-AI activities
   */
  static processNonAIActivities(teamMembersData: CrossoverTeamMemberActivity[]): NonAIDataResult {
    try {
      console.log('📱 [NON-AI-SERVICE] Processing team members data:', {
        totalMembers: teamMembersData.length,
        sampleMember: teamMembersData[0]
      });
      
      // 1. Extract non-AI groups from each team member
      const nonAIData = this.extractNonAIGroupsFromTeamMembers(teamMembersData);
      console.log('📱 [NON-AI-SERVICE] Extracted non-AI data:', {
        membersWithData: nonAIData.length,
        totalNonAITime: nonAIData.reduce((sum, member) => sum + member.totalNonAITime, 0)
      });
      
      // 2. Calculate aggregated statistics per team member
      const memberApplications = this.calculateMemberApplicationStats(nonAIData);
      console.log('📱 [NON-AI-SERVICE] Member applications calculated:', {
        membersProcessed: memberApplications.length,
        sampleMember: memberApplications[0]
      });
      
      // 3. Calculate top applications across all team members
      const topApplications = this.calculateTopApplications(nonAIData);
      console.log('📱 [NON-AI-SERVICE] Top applications calculated:', {
        topAppsCount: topApplications.length,
        topApp: topApplications[0]?.applicationName
      });
      
      return {
        teamMemberApplications: memberApplications,
        topApplications
      };
    } catch (error) {
      console.error('Error processing non-AI activities:', error);
      return {
        teamMemberApplications: [],
        topApplications: []
      };
    }
  }
  
  /**
   * Extract non-AI groups from team member data
   */
  private static extractNonAIGroupsFromTeamMembers(teamMembers: CrossoverTeamMemberActivity[]): {
    userId: string;
    email: string;
    name: string;
    nonAIGroups: CrossoverGroup[];
    totalNonAITime: number;
  }[] {
    return teamMembers.map((member, index) => {
      // Get user info from assignment
      const userId = member.assignment?.candidate?.id?.toString() || '';
      const email = member.assignment?.candidate?.email || '';
      const name = member.assignment?.candidate?.printableName || 
                   `${member.assignment?.candidate?.firstName || ''} ${member.assignment?.candidate?.lastName || ''}`.trim() || 
                   email;
      
      console.log(`📱 [NON-AI-SERVICE] Processing member ${index}:`, {
        userId,
        email,
        name,
        hasGrouping: !!member.grouping,
        groupsCount: member.grouping?.advancedGroups?.length || 0,
        allGroups: member.grouping?.advancedGroups?.map(g => g.sectionName) || []
      });
      
      // Filter out AI and PURE_AI groups, keep all others
      const nonAIGroups = this.handleMissingGroupingData(member);
      
      console.log(`📱 [NON-AI-SERVICE] Non-AI groups for ${email}:`, {
        nonAIGroupsCount: nonAIGroups.length,
        nonAIGroupNames: nonAIGroups.map(g => g.sectionName),
        nonAIGroupsWithItems: nonAIGroups.map(g => ({
          name: g.sectionName,
          itemsCount: g.groupItems.length,
          time: g.spentTime
        }))
      });
      
      // Calculate total non-AI time using standardized service for consistency
      const timeStats = calculateTimeStats({
        grouping: member.grouping || { advancedGroups: [] }
      });
      const totalNonAITime = timeStats.nonAiTime;
      
      return {
        userId,
        email,
        name,
        nonAIGroups,
        totalNonAITime
      };
    });
  }
  
  /**
   * Handle missing or malformed grouping data
   */
  private static handleMissingGroupingData(teamMember: CrossoverTeamMemberActivity): CrossoverGroup[] {
    // Handle cases where grouping data is missing or malformed
    if (!teamMember.grouping || !teamMember.grouping.advancedGroups) {
      console.warn(`No grouping data found for user ${teamMember.assignment?.candidate?.email}`);
      return [];
    }
    
    // Filter and validate groups
    return teamMember.grouping.advancedGroups.filter(group => {
      if (!group.sectionName || !group.groupItems) {
        console.warn(`Invalid group structure for user ${teamMember.assignment?.candidate?.email}:`, group);
        return false;
      }
      
      // Exclude AI and PURE_AI sections
      return group.sectionName !== "AI" && group.sectionName !== "PURE_AI";
    }).map(group => ({
      ...group,
      groupItems: this.validateGroupItems(group.groupItems)
    }));
  }
  
  /**
   * Validate and filter group items
   */
  private static validateGroupItems(groupItems: CrossoverApplication[]): CrossoverApplication[] {
    return groupItems.filter(item => {
      if (!item.applicationName || typeof item.spentTime !== 'number') {
        console.warn('Invalid group item:', item);
        return false;
      }
      return item.spentTime > 0; // Only include applications with actual usage time
    });
  }
  
  /**
   * Calculate application statistics per team member
   */
  private static calculateMemberApplicationStats(nonAIData: any[]): TeamMemberApplications[] {
    return nonAIData.map(member => {
      const applications: { [appName: string]: { hours: number; category: string; frequency: number } } = {};
      const applicationsByCategory: { [category: string]: { appName: string; hours: number }[] } = {};
      
      // Process each non-AI group
      member.nonAIGroups.forEach((group: CrossoverGroup) => {
        const categoryName = group.sectionName;
        
        if (!applicationsByCategory[categoryName]) {
          applicationsByCategory[categoryName] = [];
        }
        
        group.groupItems.forEach((item: CrossoverApplication) => {
          const hours = (item.spentTime || 0) / 60; // Convert minutes to hours
          
          if (applications[item.applicationName]) {
            applications[item.applicationName].hours += hours;
            applications[item.applicationName].frequency += 1;
          } else {
            applications[item.applicationName] = {
              hours,
              category: categoryName,
              frequency: 1
            };
          }
          
          // Add to category grouping
          applicationsByCategory[categoryName].push({
            appName: item.applicationName,
            hours
          });
        });
      });
      
      // Sort applications in each category by hours
      Object.keys(applicationsByCategory).forEach(category => {
        applicationsByCategory[category].sort((a, b) => b.hours - a.hours);
      });
      
      // Find top application
      const topApplication = Object.entries(applications)
        .sort(([,a], [,b]) => b.hours - a.hours)[0]?.[0] || 'None';
      
      return {
        userId: member.userId,
        email: member.email,
        name: member.name,
        applications,
        totalNonAIHours: member.totalNonAITime / 60, // Convert minutes to hours
        topApplication,
        applicationsByCategory
      };
    });
  }
  
  /**
   * Calculate top applications across all team members
   */
  private static calculateTopApplications(nonAIData: any[]): ApplicationUsage[] {
    const appStats: { [appName: string]: { totalHours: number; userCount: number; category: string; users: Set<string> } } = {};
    
    // Aggregate across all team members
    nonAIData.forEach(member => {
      member.nonAIGroups.forEach((group: CrossoverGroup) => {
        group.groupItems.forEach((item: CrossoverApplication) => {
          const hours = (item.spentTime || 0) / 60;
          
          if (appStats[item.applicationName]) {
            appStats[item.applicationName].totalHours += hours;
            appStats[item.applicationName].users.add(member.userId);
          } else {
            appStats[item.applicationName] = {
              totalHours: hours,
              userCount: 1,
              category: group.sectionName,
              users: new Set([member.userId])
            };
          }
        });
      });
    });
    
    // Calculate total usage for percentage
    const totalUsage = Object.values(appStats).reduce((sum, stat) => sum + stat.totalHours, 0);
    
    // Convert to ApplicationUsage array and sort by total hours
    return Object.entries(appStats)
      .map(([appName, stats]) => ({
        applicationName: appName,
        totalUsers: stats.users.size,
        totalHours: stats.totalHours,
        category: stats.category,
        percentage: totalUsage > 0 ? (stats.totalHours / totalUsage) * 100 : 0
      }))
      .sort((a, b) => b.totalHours - a.totalHours);
  }
}
